import Backbone, {ModelSetOptions} from 'backbone';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {ALL_CODONS, DEFAULT_DNA_START_CODON} from 'biojs/Codons';
import {sanitize} from 'biojs/Util';
import {READING_FRAME_NAMES} from 'constants/AlignmentConstants';
import {NucleotideType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AMINO_ACID_COLORINGS, ProteinColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import AaSequenceColoringOptionContent from 'molbio/aa-sequences/util/AaSequenceColoringOptionContent';
import {
  CDRDefinition,
  LIABILITY_SITE_DISPLAY_NAMES,
  LiabilitySiteType,
  NumberingScheme,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {LiabilitySitesDescription} from 'molbio/antibody-engineering/LiabilitySitesDescription';
import {NumberingCDRDescription} from 'molbio/antibody-engineering/NumberingCDRDescription';
import preferences from 'Preferences';
import {capitalize} from 'util/NameUtil';

export const ELN_PRINT_LAYOUT_WIDTH_WARNING_TEXT = 'The screen is not wide enough to render the print layout';

const ANNOTATIONS: VisibilityOptionConfig = {
  name: 'annotations',
  displayName: 'Annotations',
};
const PRIMERS: VisibilityOptionConfig = {
  name: 'primers',
  displayName: 'Primers',
};
const PRIMER_BASES: VisibilityOptionConfig = {
  name: 'primerBases',
  displayName: 'Primer Bases',
};
const ORFS: VisibilityOptionConfig = {
  name: 'orfs',
  displayName: 'ORFs',
};
const RNA_ORFS: VisibilityOptionConfig = {
  name: 'orfs',
  displayName: 'ORFs',
};
const CUTSITES: VisibilityOptionConfig = {
  name: 'cutsites',
  displayName: 'Cut Sites',
};
const COMPLEMENT: VisibilityOptionConfig = {
  name: 'complement',
  displayName: 'Complement',
};
const PARTS: VisibilityOptionConfig = {
  name: 'parts',
  displayName: 'Parts',
};
const PART_ANNOTATIONS: VisibilityOptionConfig = {
  name: 'partAnnotations',
  displayName: 'Parts',
};
const NUCLEOTIDE_STRUCTURE_IMAGES: VisibilityOptionConfig = {
  name: 'nucleotideStructureImages',
  displayName: 'Nucleotide Structures',
};
const OLIGO_PROPERTIES: VisibilityOptionConfig = {
  name: 'oligoProperties',
  displayName: 'Oligo Properties',
};
const ALIGNMENT_COMMENTS: VisibilityOptionConfig = {
  name: 'alignmentComments',
  displayName: 'Comments',
};
const SEQUENCE_AXIS: VisibilityOptionConfig = {
  name: 'sequenceAxis',
  displayName: 'Axis',
};
const HOMOLOGY_REGION_AXIS: VisibilityOptionConfig = {
  name: 'homologyRegionAxis',
  displayName: 'Axis',
};
const SEQUENCE_ALIGNMENT_AXIS: VisibilityOptionConfig = {
  name: 'sequenceAxis',
  displayName: 'Alignment Axis',
};
const AA_SEQUENCE_AXIS: VisibilityOptionConfig = {
  name: 'proteinAxis',
  displayName: 'Axis',
};
const GAPTOREALAXIS: VisibilityOptionConfig = {
  name: 'gapToRealAxis',
  displayName: 'Sequence Axis',
};
const TRANSLATIONS: VisibilityOptionConfig = {
  name: 'translations',
  displayName: 'Translations',
};
const TRANSCRIPTIONS: VisibilityOptionConfig = {
  name: 'transcriptions',
  displayName: 'Transcriptions',
};
const READING_FRAMES: VisibilityOptionConfig = {
  name: 'readingFrames',
  displayName: 'Reading Frames',
};
const AA_INDICES: VisibilityOptionConfig = {
  name: 'aminoAcidIndices',
  displayName: 'Amino Acid Indices',
};
const OVERHANGS: VisibilityOptionConfig = {
  name: 'overhangs',
  displayName: 'Show Cuts',
};
const DNA: VisibilityOptionConfig = {
  name: 'dna',
  displayName: 'DNA',
};
const HOMOLOGY_REGION_DNA: VisibilityOptionConfig = {
  name: 'homologyRegionDNA',
  displayName: 'DNA',
};
const AMINO_ACIDS: VisibilityOptionConfig = {
  name: 'aminoAcids',
  displayName: 'Amino Acids',
};
const VOTES: VisibilityOptionConfig = {
  name: 'votes',
  displayName: 'Votes',
};
const LINE: VisibilityOptionConfig = {
  name: 'line',
  displayName: 'Mismatches',
};
const TRACE: VisibilityOptionConfig = {
  name: 'trace',
  displayName: 'Trace',
};
const TRACE_QUALITY: VisibilityOptionConfig = {
  name: 'trace-quality',
  displayName: 'Trace Quality',
};
const EXPAND_MINI_MAP: VisibilityOptionConfig = {
  name: 'miniMap',
  displayName: 'Expanded Mini-Map',
};
const MAXIMISE: VisibilityOptionConfig = {
  name: 'maximise',
  displayName: 'Expanded View',
};
const SEARCH_BASES: VisibilityOptionConfig = {
  name: 'searchdna',
  displayName: 'Search Results',
};
const SEARCH_AMINO_ACIDS: VisibilityOptionConfig = {
  name: 'searchAminoAcids',
  displayName: 'Search Results',
};
const INLINE_LABELS: VisibilityOptionConfig = {
  name: 'inlinelabels',
  displayName: 'Inline Labels',
};
const NOTE_TIMESTAMPS: VisibilityOptionConfig = {
  name: 'noteTimestamps',
  displayName: 'Timestamps',
};
const ELN_SPELL_CHECK: VisibilityOptionConfig = {
  name: 'elnSpellCheck',
  displayName: 'Spell Check',
  fullSync: true,
  type: 'eln', // use the same type regardless if used in entries or protocols (ensures using same preference)
};

const ELN_SHOW_PRINT_MARGINS = {
  name: 'print-layout-show-margins',
  displayName: 'Show Print Margins',
  display: 'check-option',
  value: false,
} satisfies VisibilityOptionSettingCheckOptionConfig;

const ELN_PRINT_LAYOUT: VisibilityOptionConfig = {
  name: 'elnPrintLayout',
  displayName: 'Print Layout',
  cssWarning: ELN_PRINT_LAYOUT_WIDTH_WARNING_TEXT,
  settings: [ELN_SHOW_PRINT_MARGINS],
};

const ELN_TABLE_OF_CONTENTS: VisibilityOptionConfig = {
  name: 'elnTableOfContents-v2',
  displayName: 'Table of Contents',
};

const ELN_SHOW_FORMULAS: VisibilityOptionConfig = {
  name: 'elnShowFormulas',
  displayName: 'Show Formulas',
};

const ELN_HIGHLIGHT_CUSTOM_COLUMNS: VisibilityOptionConfig = {
  name: 'elnHighlightCustomColumns',
  displayName: 'Highlight Custom Columns',
};

const ELN_READ_ONLY_MODE: VisibilityOptionConfig = {
  name: 'elnReadOnlyMode',
  displayName: 'Read Only Mode',
  type: 'eln',
};

const AA_SEQUENCE_COLORING: VisibilityOptionConfig = {
  name: 'coloring',
  displayName: 'Coloring Scheme',
};

const PROTEIN_HYDROPHOBICITY_GRAPH: VisibilityOptionConfig = {
  name: 'proteinHydrophobicityGraph',
  displayName: 'Hydrophobicity',
};

const PROTEIN_PI_GRAPH: VisibilityOptionConfig = {
  name: 'proteinPIGraph',
  displayName: 'pI',
};

const PROTEIN_ALIGNMENT_IDENTITY_GRAPH: VisibilityOptionConfig = {
  name: 'proteinAlignmentIdentityGraph',
  displayName: 'Identity',
};

const SUMMARY_IDENTITY_GRAPH: VisibilityOptionConfig = {
  name: 'summaryIdentityGraph',
  displayName: 'Summary Identity',
};

const ALIGNMENT_ROW_STATS: VisibilityOptionConfig = {
  name: 'alignmentRowStatistics',
  displayName: 'Row Statistics',
};

const NUMBERING_SETTINGS: Array<VisibilityOptionSettingSelectOptionConfig<string>> = [
  {
    name: 'numberingScheme',
    displayName: 'Numbering Scheme',
    display: 'select-option',
    value: 'Chothia',
    options: Object.values(NumberingScheme).map((scheme) => buildSelectOption(capitalize(scheme))),
    parseStringValue(val: string): string {
      return val;
    },
  },
];

const ANTIBODY_NUMBERING: VisibilityOptionConfig = {
  name: 'antibodyNumbering',
  descriptionComponent: NumberingCDRDescription,
  displayName: 'Antibody Numbering',
  settings: NUMBERING_SETTINGS,
};

const CDR_SETTINGS: Array<VisibilityOptionSettingSelectOptionConfig<string>> = [
  {
    name: 'CDRDefinition',
    displayName: 'CDR Definition',
    display: 'select-option',
    value: 'Chothia',
    options: Object.values(CDRDefinition).map((definition) => buildSelectOption(capitalize(definition))),
    parseStringValue(val: string): string {
      return val;
    },
  },
];

const ANTIBODY_CDR_ANNOTATIONS: VisibilityOptionConfig = {
  name: 'antibodyCDRAnnotations',
  descriptionComponent: NumberingCDRDescription,
  displayName: 'CDR Annotations',
  settings: CDR_SETTINGS,
};

const LIABILITY_SITE_SETTINGS: Array<VisibilityOptionSettingCheckOptionConfig> = Object.values(
  LiabilitySiteType
).map((siteType: LiabilitySiteType) => ({
  name: siteType,
  displayName: LIABILITY_SITE_DISPLAY_NAMES[siteType],
  display: 'check-option',
  value: true,
}));

const PROTEIN_LIABILITY_SITES: VisibilityOptionConfig = {
  name: 'proteinLiabilitySites',
  descriptionComponent: LiabilitySitesDescription,
  displayName: 'Liability Sites',
  settings: LIABILITY_SITE_SETTINGS,
};

const ALIGNMENT_BASE_QUALITY_CAPITALIZATION: VisibilityOptionConfig = {
  name: 'alignmentBaseQualityCapitalization',
  displayName: `Quality-based capitalization (${BenchlingConfig.MOLBIO_ALIGNMENT_CAPITALIZATION_QUALITY_THRESHOLD})`,
};

export interface SettingSelectOption {
  value: string;
  display: ReactNode;
}

export type FeatureCollectionName =
  | 'annotations'
  | 'sequencePrimers'
  | 'parts'
  | 'translations'
  | 'transcriptions';
export type FeatureCounts = Partial<Record<FeatureCollectionName, number>>;

// Create a global singleton object to which fullSync visibility options can subscribe to stay in sync with other
// instances of the same fullSync visibility option.
// eslint-disable-next-line no-restricted-syntax
const fullSyncNotifier = _.extend({}, Backbone.Events);

export function buildSelectOption(val: string): SettingSelectOption {
  return {value: val, display: val};
}

function generateORFSettings(nucleotideType: NucleotideType): Array<VisibilityOptionSettingConfig> {
  const rnaFramesOptions = ['All', '1', '2', '3'].map((frame) => buildSelectOption(frame));
  const dnaFramesOptions = ['All', 'Forward', 'Reverse', '1', '2', '3', '-1', '-2', '-3'].map((frame) =>
    buildSelectOption(frame)
  );
  return [
    {
      name: 'minimum-orf-length',
      displayName: 'Minimum ORF length (bases)',
      display: 'number-option',
      value: 60,
      parseStringValue(val: string): number {
        if (parseInt(val, 10) >= 0) {
          return parseInt(val, 10);
        } else {
          return 60;
        }
      },
    },
    {
      name: 'orf-frame',
      displayName: 'Frames',
      display: 'select-option',
      value: 'All',
      options: nucleotideType === NucleotideType.RNA ? rnaFramesOptions : dnaFramesOptions,
      parseStringValue(val: string): string | number {
        const intVal = parseInt(val, 10);
        if ([1, 2, 3, -1, -2, -3].includes(intVal)) {
          return intVal;
        } else if (['Forward', 'Reverse'].includes(val)) {
          return val;
        } else {
          return 'All';
        }
      },
    },
    {
      name: 'show-orf-amino-acids',
      displayName: 'Show Amino Acids (Sequence Map)',
      display: 'check-option',
      value: false,
    },
    {
      name: 'orf-start-codon',
      displayName: 'Start Codon',
      display: 'select-option',
      value: sanitize(DEFAULT_DNA_START_CODON, nucleotideType),
      options: ALL_CODONS.map((codon) => ({
        value: codon,
        display: sanitize(codon, nucleotideType),
      })),
      parseStringValue(val: string): string {
        return val;
      },
    },
  ];
}

const ORF_SETTINGS = generateORFSettings(NucleotideType.DNA);
const RNA_ORF_SETTINGS = generateORFSettings(NucleotideType.RNA);

// Construct protein coloring settings in a function because it depends on a flag
function getAaSequenceColoringSetting(): VisibilityOptionSettingSelectOptionConfig<ProteinColorScheme> {
  return {
    name: 'coloring-scheme',
    displayName: 'Coloring Scheme',
    display: 'select-option',
    value: ProteinColorScheme.RASMOL,
    options: Object.keys(AMINO_ACID_COLORINGS).map((coloring: ProteinColorScheme) => ({
      value: coloring,
      display: <AaSequenceColoringOptionContent key={coloring} colorScheme={coloring} />,
    })),
    parseStringValue(val: string): ProteinColorScheme {
      return val as ProteinColorScheme;
    },
  };
}

// Construct translation coloring settings in a function because it depends on a flag
function getTranslationColoringSetting(): VisibilityOptionSettingSelectOptionConfig<ProteinColorScheme> {
  return {
    // Translations support an additional color scheme of None so we save it under a different key.
    name: 'translation-coloring-scheme',
    displayName: 'Coloring Scheme',
    display: 'select-option',
    value: ProteinColorScheme.RASMOL,
    options: Object.keys(AMINO_ACID_COLORINGS)
      .concat(ProteinColorScheme.NONE)
      .map((coloring: ProteinColorScheme) => ({
        value: coloring,
        display: <AaSequenceColoringOptionContent key={coloring} colorScheme={coloring} />,
      })),
    parseStringValue(val: string): ProteinColorScheme {
      return val as ProteinColorScheme;
    },
  };
}

const IDENTITY_SLIDING_WINDOW_SETTING: VisibilityOptionSettingNumberOptionConfig = {
  name: 'sliding-window-size',
  displayName: 'Sliding Window Size',
  display: 'number-option',
  value: 5,
  parseStringValue(val: string): number {
    if (parseInt(val, 10) >= 1) {
      return parseInt(val, 10);
    } else {
      return 1;
    }
  },
};

const SUMMARY_IDENTITY_GRAPH_SETTINGS = [IDENTITY_SLIDING_WINDOW_SETTING];

const READING_FRAMES_SETTINGS: Array<VisibilityOptionSettingCheckOptionConfig> = READING_FRAME_NAMES.map(
  (frameName) => ({
    name: frameName,
    displayName: `Show frame ${frameName}`,
    display: 'check-option',
    value: true,
  })
);

type SettingDisplay = 'number-option' | 'check-option' | 'select-option';

interface BaseVisibilityOptionSettingConfig<V> {
  name: string;
  displayName: ReactNode;
  display: SettingDisplay;
  value: V;
}

interface VisibilityOptionSettingNumberOptionConfig extends BaseVisibilityOptionSettingConfig<number> {
  display: 'number-option';
  parseStringValue(val: string): number;
}

interface VisibilityOptionSettingSelectOptionConfig<T> extends BaseVisibilityOptionSettingConfig<T> {
  display: 'select-option';
  options: Array<SettingSelectOption>;
  parseStringValue(val: string): T;
}

interface VisibilityOptionSettingCheckOptionConfig extends BaseVisibilityOptionSettingConfig<boolean> {
  display: 'check-option';
}

export type VisibilityOptionSettingConfig =
  | VisibilityOptionSettingNumberOptionConfig
  | VisibilityOptionSettingSelectOptionConfig<unknown>
  | VisibilityOptionSettingCheckOptionConfig;

export class VisibilityOptionSetting extends preferences.PreferenceModel {
  localOptions!: Array<VisibilityOption>;

  // TODO get rid of defaults, or do it manually
  override defaults(): {
    name: null;
    displayName: null;
    display: null;
    value: null;
    sync: true;
    parseStringValue(val: string): string;
  } {
    return {
      name: null,
      displayName: null,
      display: null,
      value: null,
      sync: true,
      parseStringValue(val: string): string {
        return val;
      },
    };
  }

  override initialize(
    _attributes: VisibilityOptionSettingConfig,
    options: {optionName: string; type: FeatureName}
  ): void {
    const {type} = options;
    const {optionName} = options;
    this.prefix = `${type}.${optionName}.${this.get('name')}`;
    this.preservedAttributes = this.get('sync') ? ['value'] : [];
    this.localOptions = [];
    super.initialize();
  }
}

export class VisibilityOptionSettings extends Backbone.Collection<VisibilityOptionSetting> {
  static initClass(): void {
    this.prototype.model = VisibilityOptionSetting;
  }
}

VisibilityOptionSettings.initClass();

export interface MakeLocalSyncOptions {
  globalSync?: boolean;
  editableOptions?: Array<string> | null;
}

type FeatureName =
  | 'alignment'
  | 'circular'
  | 'eln'
  | 'linear'
  | 'minimap'
  | 'note'
  | 'protein_alignment'
  | 'protein_alignment_summary'
  | 'protocol'
  | 'test';

export interface VisibilityOptionConfig {
  name: string;
  cssWarning?: string;
  descriptionComponent?: React.FC;
  displayName: string;
  fullSync?: boolean;
  type?: FeatureName | null;
  indeterminate?: boolean;
  settings?: VisibilityOptionSettings | Array<VisibilityOptionSettingConfig>;
}

interface VisibilityOptionAttributes extends VisibilityOptionConfig {
  editable: boolean;
  visible: boolean;
  sync: boolean;
  indeterminate: boolean;
  disabled: boolean;
  settings: VisibilityOptionSettings;
}

interface VOModelSetOptions extends ModelSetOptions {
  type: string;
  noSave: boolean;
  sync: boolean;
}

export interface VisibilityOption {
  get<K extends keyof VisibilityOptionAttributes>(key: K): VisibilityOptionAttributes[K];
  set(obj: Partial<VisibilityOptionAttributes>, options?: Partial<VOModelSetOptions>): VisibilityOption;
  set<K extends keyof VisibilityOptionAttributes>(
    attributeName: K,
    value: VisibilityOptionAttributes[K],
    options?: Partial<VOModelSetOptions>
  ): VisibilityOption;
}

export class VisibilityOption extends preferences.PreferenceModel {
  localOptions!: Array<VisibilityOption>;

  // TODO get rid of defaults
  override defaults(): VisibilityOptionAttributes {
    return {
      // @ts-expect-error
      name: null,
      // @ts-expect-error
      displayName: null,
      editable: true,
      visible: true,
      sync: false,
      type: null,
      indeterminate: false,
      display: null,
      settings: new VisibilityOptionSettings(),
      disabled: false,
    };
  }

  override initialize(): void {
    const type = this.get('type');
    this.prefix = `${type}.${this.get('name')}`;
    this.preservedAttributes = this.get('sync') ? ['visible'] : [];
    this.localOptions = [];
    this.listenTo(
      this,
      'change:visible',
      function (
        this: VisibilityOption,
        model: VisibilityOption,
        value: VisibilityOptionAttributes['visible'],
        opts: {type: string}
      ): void {
        if (opts.type === 'sync') {
          return;
        }
        this.set('indeterminate', false);
        this.trigger('redraw');
      }
    );

    // TODO initialize visibility option settings outside of initializing the visibility option
    if (Array.isArray(this.get('settings'))) {
      this.set(
        'settings',
        // @ts-expect-error We allow passing in a settings array when initializing
        new VisibilityOptionSettings(this.get('settings'), {
          type,
          optionName: this.get('name'),
        }),
        {sync: this.get('sync')}
      );
    }

    if (this.get('fullSync')) {
      this.on(
        'change:visible',
        function (
          this: VisibilityOption,
          model: VisibilityOption,
          value: VisibilityOptionAttributes['visible'],
          opts: {type: string}
        ): void {
          // Avoid infinite loop - if one VisibilityOption triggers a change event on the fullSyncNotifier,
          // then we need to avoid transmitting the same change back to the notifier. Otherwise, the event would
          // bounce back-and-forth in an infinite loop.
          if (opts.type !== 'fullSync') {
            fullSyncNotifier.trigger(`change:${this.prefix}`, model, value);
          }
        }
      );
      this.listenTo(
        fullSyncNotifier,
        `change:${this.prefix}`,
        function (this: VisibilityOption, model: Backbone.Events, value: boolean): void {
          this.set({visible: value}, {type: 'fullSync'});
        }
      );
    }

    this.listenTo(this.get('settings'), 'change:value', function (this: VisibilityOption): void {
      this.trigger('redraw');
    });
    super.initialize();
  }

  getSetting(name: string): VisibilityOptionSetting | undefined {
    return this.get('settings').findWhere({name});
  }

  isSettingChecked(name: string): boolean {
    // We cast to boolean for backwards-compatibility: older values are 'checked'/null rather than true/false
    return Boolean(this.get('settings').findWhere({name})?.get('value'));
  }

  addLocalSync(model: VisibilityOption): void {
    this.localOptions.push(model);
    this.listenTo(model, 'change:visible', this.localSync.bind(this));
    model.listenTo(this, 'change:visible', model.globalSync.bind(model));
  }

  globalSync(model: VisibilityOption, value: boolean, options: {type: string}): void {
    if (options.type === 'sync') {
      return;
    }
    this.set('visible', value, {type: 'sync'});
  }

  localSync(model: VisibilityOption, value: unknown, options: {type: string}): void {
    let visible;
    if (options.type === 'sync') {
      return;
    }
    let someOn = false;
    let allOn = true;
    for (const option of this.localOptions) {
      visible = option.get('visible');
      if (!someOn) {
        someOn = visible;
      }
      if (allOn) {
        allOn = visible;
      }
    }
    // if some but not all are on, we are indeterminate.
    this.set({visible: someOn, indeterminate: someOn && !allOn}, {type: 'sync'});
    this.trigger('redraw');
  }
}

export class VisibilityOptions extends Backbone.Collection<VisibilityOption> {
  values!: {[key: string]: VisibilityOption};

  static initClass(): void {
    this.prototype.model = VisibilityOption;
  }

  override initialize(): void {
    this.checkModel = this.checkModel.bind(this);
    //  Already bound above
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.on('change:visible', this.checkModel);
  }

  _cascadeDependentSetting(
    model: VisibilityOption,
    parentSetting: VisibilityOptionConfig,
    dependentSetting: VisibilityOptionConfig
  ): void {
    if (model.get('name') !== parentSetting.name) {
      return;
    }
    if (model.get('visible')) {
      this.getOption(dependentSetting.name)?.set({disabled: false});
    } else {
      this.getOption(dependentSetting.name)?.set({visible: false, disabled: true});
    }
  }

  checkModel(model: VisibilityOption): void {
    this._cascadeDependentSetting(model, PRIMERS, PRIMER_BASES);
    this._cascadeDependentSetting(model, TRACE, TRACE_QUALITY);
    this._cascadeDependentSetting(model, TRANSLATIONS, AA_INDICES);
  }

  makeLookup(): void {
    this.values = {};
    for (const model of this.models) {
      this.values[model.get('name')] = model;
    }
  }

  getOption(name: string): VisibilityOption | undefined {
    return this.values[name];
  }

  getEditableOptions(): Array<VisibilityOption> {
    return this.filter((opt) => opt.get('editable'));
  }

  // TODO make editableOption strings from a union type of all the known options
  makeLocalSync(options?: MakeLocalSyncOptions): VisibilityOptions {
    if (options == null) {
      options = {};
    }
    options.globalSync = options.globalSync != null ? options.globalSync : true;
    options.editableOptions = options.editableOptions != null ? options.editableOptions : null;

    const allEditable = options.editableOptions == null;
    const copy = new VisibilityOptions(
      this.models.map((model) => {
        const option = model.toJSON();
        option.editable = allEditable || options.editableOptions!.includes(option.name);
        option.indeterminate = false;
        option.sync = false;
        const copyModel = new VisibilityOption(option);
        if (options.globalSync) {
          model.addLocalSync(copyModel);
        }
        return copyModel;
      })
    );
    copy.makeLookup();
    // TODO just call checkModel during initialization and remove here
    // eslint-disable-next-line @typescript-eslint/unbound-method
    copy.each(this.checkModel);
    return copy;
  }
}

VisibilityOptions.initClass();

// Views initialize by specifying the "base" option plus overrides
export interface VisibilityOptionConstructionOption extends Partial<VisibilityOptionAttributes> {
  base: VisibilityOptionConfig;
}

export function constructOptions(
  type: FeatureName,
  opts: Array<VisibilityOptionConstructionOption>
): VisibilityOptions {
  initialize();
  const v = new VisibilityOptions(
    opts.map((opt) => _(_(opt).omit('base')).defaults(opt.base, {type, sync: true}))
  );
  v.makeLookup();
  return v;
}

let hasInitialized = false;

function initialize(): void {
  if (!hasInitialized) {
    hasInitialized = true;
    ORFS.settings = new VisibilityOptionSettings(ORF_SETTINGS, {
      type: 'linear',
      optionName: ORFS.name,
      sync: true,
    });
    RNA_ORFS.settings = new VisibilityOptionSettings(RNA_ORF_SETTINGS, {
      type: 'linear',
      optionName: RNA_ORFS.name,
      sync: true,
    });
    AA_SEQUENCE_COLORING.settings = new VisibilityOptionSettings([getAaSequenceColoringSetting()], {
      type: 'linear',
      optionName: AA_SEQUENCE_COLORING.name,
      sync: true,
    });
    TRANSLATIONS.settings = new VisibilityOptionSettings([getTranslationColoringSetting()], {
      type: 'linear',
      optionName: AA_SEQUENCE_COLORING.name,
      sync: true,
    });
    SUMMARY_IDENTITY_GRAPH.settings = new VisibilityOptionSettings(SUMMARY_IDENTITY_GRAPH_SETTINGS, {
      type: 'linear',
      optionName: SUMMARY_IDENTITY_GRAPH.name,
      sync: true,
    });
    READING_FRAMES.settings = new VisibilityOptionSettings(READING_FRAMES_SETTINGS, {
      type: 'linear',
      optionName: READING_FRAMES.name,
    });
  }
}

export {
  AA_INDICES,
  ALIGNMENT_BASE_QUALITY_CAPITALIZATION,
  ALIGNMENT_COMMENTS,
  ALIGNMENT_ROW_STATS,
  ANNOTATIONS,
  COMPLEMENT,
  CUTSITES,
  DNA,
  ELN_PRINT_LAYOUT,
  ELN_SHOW_PRINT_MARGINS,
  ELN_SPELL_CHECK,
  ELN_TABLE_OF_CONTENTS,
  ELN_SHOW_FORMULAS,
  ELN_HIGHLIGHT_CUSTOM_COLUMNS,
  ELN_READ_ONLY_MODE,
  GAPTOREALAXIS,
  getAaSequenceColoringSetting,
  getTranslationColoringSetting,
  HOMOLOGY_REGION_DNA,
  IDENTITY_SLIDING_WINDOW_SETTING,
  initialize,
  INLINE_LABELS,
  LINE,
  MAXIMISE,
  EXPAND_MINI_MAP,
  ORFS,
  RNA_ORFS,
  OVERHANGS,
  PART_ANNOTATIONS,
  NOTE_TIMESTAMPS,
  NUCLEOTIDE_STRUCTURE_IMAGES,
  OLIGO_PROPERTIES,
  PARTS,
  PRIMER_BASES,
  PRIMERS,
  PROTEIN_ALIGNMENT_IDENTITY_GRAPH,
  AA_SEQUENCE_AXIS,
  AA_SEQUENCE_COLORING,
  PROTEIN_HYDROPHOBICITY_GRAPH,
  ANTIBODY_CDR_ANNOTATIONS,
  ANTIBODY_NUMBERING,
  PROTEIN_LIABILITY_SITES,
  PROTEIN_PI_GRAPH,
  AMINO_ACIDS,
  READING_FRAMES,
  SEARCH_BASES,
  SEARCH_AMINO_ACIDS,
  SEQUENCE_AXIS,
  HOMOLOGY_REGION_AXIS,
  SEQUENCE_ALIGNMENT_AXIS,
  SUMMARY_IDENTITY_GRAPH,
  TRACE,
  TRACE_QUALITY,
  TRANSCRIPTIONS,
  TRANSLATIONS,
  VOTES,
};
